import { authFetch } from '../../utils/authFetch';
import { defineStoredJson } from '../../utils/storage';
import type { AuthUser } from './useAuth';

/**
 * Encrypted-at-rest offline credential. On a successful ONLINE login we mint a
 * long-lived token, encrypt it (plus the user identity) under a key derived from
 * the user's password, and cache it on-device. Offline, re-entering the password
 * re-derives the key and decrypts it — so the decryption itself IS the password
 * check: a wrong password fails the AES-GCM auth tag and nothing usable (no
 * password, no verifier) is ever stored in the clear. Requires a secure context
 * (HTTPS/localhost) for WebCrypto — already guaranteed by the deployment.
 */

const PBKDF2_ITERATIONS = 600_000;

export interface OfflinePayload {
    token: string;
    user: AuthUser;
}

// One entry per username so a shared device supports several field workers.
interface StoredEntry {
    salt: string;
    iv: string;
    data: string;
    updatedAt: string;
}
type Store = Record<string, StoredEntry>;

/**
 * Never a cache: without it there is no signing in on a device with no signal.
 * The `_v1` is part of the name, not the layer's business — a second version
 * would be a second key, deliberately, so a half-decrypted old one is never
 * mistaken for the new shape.
 */
const credentials = defineStoredJson<Store>({
    key: 'offline_cred_v1',
    fallback: () => ({}),
    keepOnWipe: true
});

function readStore(): Store {
    return credentials.read();
}

function writeStore(store: Store): void {
    credentials.write(store);
}

function toBase64(bytes: Uint8Array): string {
    let binary = '';
    for (const byte of bytes) {
        binary += String.fromCharCode(byte);
    }
    return btoa(binary);
}

function fromBase64(value: string): Uint8Array<ArrayBuffer> {
    const binary = atob(value);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
}

async function deriveKey(
    password: string,
    salt: Uint8Array<ArrayBuffer>
): Promise<CryptoKey> {
    const baseKey = await crypto.subtle.importKey(
        'raw',
        new TextEncoder().encode(password),
        'PBKDF2',
        false,
        ['deriveKey']
    );
    return crypto.subtle.deriveKey(
        {
            name: 'PBKDF2',
            salt,
            iterations: PBKDF2_ITERATIONS,
            hash: 'SHA-256'
        },
        baseKey,
        { name: 'AES-GCM', length: 256 },
        false,
        ['encrypt', 'decrypt']
    );
}

/** Encrypt and store an offline credential for a user. */
export async function saveOfflineCredential(
    username: string,
    password: string,
    payload: OfflinePayload
): Promise<void> {
    const salt = crypto.getRandomValues(new Uint8Array(16));
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const key = await deriveKey(password, salt);
    const plaintext = new TextEncoder().encode(JSON.stringify(payload));
    const cipher = new Uint8Array(
        await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, plaintext)
    );

    const store = readStore();
    store[username] = {
        salt: toBase64(salt),
        iv: toBase64(iv),
        data: toBase64(cipher),
        updatedAt: new Date().toISOString()
    };
    writeStore(store);
}

/**
 * Decrypt the cached offline credential with the entered password. Returns null
 * when there is no cached credential or the password is wrong (indistinguishable
 * by design — a wrong password simply fails to decrypt).
 */
export async function loadOfflineCredential(
    username: string,
    password: string
): Promise<OfflinePayload | null> {
    const entry = readStore()[username];
    if (!entry) {
        return null;
    }
    try {
        const key = await deriveKey(password, fromBase64(entry.salt));
        const plain = await crypto.subtle.decrypt(
            { name: 'AES-GCM', iv: fromBase64(entry.iv) },
            key,
            fromBase64(entry.data)
        );
        return JSON.parse(new TextDecoder().decode(plain)) as OfflinePayload;
    } catch {
        return null;
    }
}

/** Mint a long-lived offline token for the current (already-authenticated) user. */
async function mintOfflineToken(): Promise<string | null> {
    try {
        const res = await authFetch('/api/auth/offline-token', {
            method: 'POST'
        });
        if (!res.ok) {
            return null;
        }
        const data = await res.json();
        return typeof data.token === 'string' ? data.token : null;
    } catch {
        return null;
    }
}

/**
 * After an online login, mint + cache an encrypted offline credential so the user
 * can log in offline later. Best-effort: any failure is swallowed (the user is
 * already logged in — they simply won't have offline access until it succeeds).
 */
export async function cacheOfflineCredential(
    username: string,
    password: string,
    user: AuthUser
): Promise<void> {
    const token = await mintOfflineToken();
    if (!token) {
        return;
    }
    await saveOfflineCredential(username, password, { token, user });
}
